import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { mpApi, SERVICE_KEYS, serviceMeta, chipProps, monthLabel, shiftMonth } from "./mpShared";

const MODES = [
  ["targets", "Generate from targets", "Creates one task per missing deliverable (e.g. 7 Creatives → 7 separate tasks), spread across working days."],
  ["copy", "Copy previous month", "Same as above, but reuses last month's task names and descriptions."],
  ["copy_people", "Copy previous month + assigned employees", "Also gives each task to whoever did the matching task last month."],
  ["copy_schedule", "Copy previous month + assignees + schedule", "Also keeps last month's day-of-month for each task."],
  ["blank", "Start blank", "Create no tasks now — targets still apply and you add tasks by hand."],
];

/** "Generate Monthly Tasks" / "Create New Month". Only ever tops up missing slots. */
export default function GenerateDialog({ open, onClose, month, businessId, clients, members, onDone }) {
  const [mode, setMode] = useState("targets");
  const [clientId, setClientId] = useState("");
  const [assignee, setAssignee] = useState("");
  const [skipSundays, setSkipSundays] = useState(true);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setMode("targets"); setClientId(""); setAssignee(""); } }, [open]);

  // Missing slots = target − tasks already created, per service.
  const plan = useMemo(() => clients
    .filter((c) => c.target_total > 0 && (!clientId || c.client_id === clientId))
    .map((c) => {
      const perService = {};
      let missing = 0;
      for (const k of SERVICE_KEYS) { if (!c.by_service[k]) continue; const m = Math.max(0, c.by_service[k].target - c.by_service[k].total); if (m) { perService[k] = m; missing += m; } }
      return { client_id: c.client_id, name: c.name, perService, missing };
    }), [clients, clientId]);
  const totalMissing = plan.reduce((s, p) => s + p.missing, 0);
  const prev = shiftMonth(month, -1);

  const run = async () => {
    if (mode === "blank") { toast.success(`${monthLabel(month)} started blank — targets apply, add tasks as you go.`); onClose(); return; }
    setBusy(true);
    try {
      const body = {
        month, business_id: businessId || undefined, client_id: clientId || undefined, skip_sundays: skipSundays,
        assigned_users: assignee ? [assignee] : [],
        copy_from: mode.startsWith("copy") ? prev : undefined,
        copy_assignees: mode === "copy_people" || mode === "copy_schedule",
        copy_schedule: mode === "copy_schedule",
      };
      const r = await mpApi("/generate", { method: "POST", body });
      toast.success(r.created ? `${r.created} task${r.created === 1 ? "" : "s"} created for ${monthLabel(month)}` : "Every target already has its tasks");
      onDone(); onClose();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto rounded-2xl">
        <DialogHeader><DialogTitle>Generate monthly tasks — {monthLabel(month)}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            {MODES.map(([k, label, hint]) => (
              <label key={k} className={`flex cursor-pointer gap-3 rounded-xl border p-3 ${mode === k ? "border-indigo-400 bg-indigo-50/50" : "border-slate-200 hover:bg-slate-50"}`}>
                <input type="radio" name="mode" checked={mode === k} onChange={() => setMode(k)} className="mt-1" />
                <span>
                  <span className="block text-sm font-medium text-slate-900">{label}{k.startsWith("copy") && <span className="font-normal text-slate-500"> (from {monthLabel(prev)})</span>}</span>
                  <span className="block text-xs text-slate-500">{hint}</span>
                </span>
              </label>
            ))}
          </div>

          {mode !== "blank" && (
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="text-xs font-medium text-slate-600">Clients</label>
                <select value={clientId} onChange={(e) => setClientId(e.target.value)} className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-2 text-sm">
                  <option value="">All clients with targets</option>
                  {clients.filter((c) => c.target_total > 0).map((c) => <option key={c.client_id} value={c.client_id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Assign new tasks to {mode === "copy_people" || mode === "copy_schedule" ? "(when last month had no one)" : "(optional)"}</label>
                <select value={assignee} onChange={(e) => setAssignee(e.target.value)} className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-2 text-sm">
                  <option value="">Leave unassigned</option>
                  {members.map((m) => <option key={m.user_id} value={m.user_id}>{m.name}</option>)}
                </select>
              </div>
              <label className="inline-flex items-center gap-2 text-sm text-slate-600 sm:col-span-2"><input type="checkbox" checked={skipSundays} onChange={(e) => setSkipSundays(e.target.checked)} /> Don't schedule on Sundays</label>
            </div>
          )}

          {mode !== "blank" && (
            <div className="rounded-xl border border-slate-200">
              <div className="border-b border-slate-100 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Will create {totalMissing} task{totalMissing === 1 ? "" : "s"}</div>
              <div className="max-h-56 divide-y divide-slate-100 overflow-y-auto">
                {plan.length === 0 ? <div className="px-3 py-4 text-sm text-slate-500">No clients have targets. Set them in the Service Targets tab.</div>
                  : plan.map((p) => (
                    <div key={p.client_id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
                      <span className="font-medium text-slate-800">{p.name}</span>
                      {p.missing === 0 ? <span className="text-xs text-green-600">All tasks already created</span> : (
                        <span className="flex flex-wrap gap-1">
                          {Object.entries(p.perService).map(([k, n]) => <span key={k} {...chipProps(k, "rounded px-1.5 py-0.5 text-[11px] font-semibold")}>{n} {serviceMeta(k).short}</span>)}
                        </span>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
        <div className="mt-2 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-lg">Cancel</Button>
          <Button onClick={run} disabled={busy || (mode !== "blank" && totalMissing === 0)} data-testid="mp-generate-run" className="rounded-lg bg-emerald-600 text-white hover:bg-emerald-700">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="mr-1.5 h-4 w-4" />{mode === "blank" ? "Start blank" : `Create ${totalMissing} task${totalMissing === 1 ? "" : "s"}`}</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
